const {
    createEventId,
    recordDiagnostic,
} = require('./diagnostics');
const {pushDataLayerEvent} = require('./dataLayer');

const STORAGE_KEY = 'kd.navigation-analytics-events.v2';
const EVENT_LIFETIME_MS = 24 * 60 * 60 * 1000;

const TARGET_PAGE = Object.freeze({
    ANY: 'any',
    CART: 'cart',
    CHECKOUT: 'checkout',
    ORDER_CONFIRMATION: 'order_confirmation',
});

function readQueue(storage, now) {
    const serializedQueue = storage.getItem(STORAGE_KEY);

    if (!serializedQueue) {
        return [];
    }

    let queue;

    try {
        queue = JSON.parse(serializedQueue);
    } catch (error) {
        storage.removeItem(STORAGE_KEY);
        recordDiagnostic('queue_corrupt', {
            source: 'navigation_queue',
        });
        return [];
    }

    if (!Array.isArray(queue)) {
        return [];
    }

    return queue.filter(function (entry) {
        const hasValidShape = entry &&
            Array.isArray(entry.events) &&
            Array.isArray(entry.eventIds) &&
            entry.events.length === entry.eventIds.length &&
            typeof entry.targetPage === 'string' &&
            Number.isFinite(entry.expiresAt);

        if (!hasValidShape) {
            recordDiagnostic('queue_corrupt', {
                source: 'navigation_queue',
            });
            return false;
        }

        if (entry.expiresAt <= now) {
            entry.events.forEach(function (eventData, index) {
                recordDiagnostic('queue_expired', {
                    eventId: entry.eventIds[index],
                    eventName: eventData.event,
                    source: 'navigation_queue',
                    targetPage: entry.targetPage,
                });
            });
            return false;
        }

        return true;
    });
}

function writeQueue(storage, queue) {
    if (queue.length === 0) {
        storage.removeItem(STORAGE_KEY);
        return;
    }

    storage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

/**
 * Persists navigation-bound events so the destination document can emit them.
 * If sessionStorage is unavailable, emitting on the current page is safer than
 * silently dropping the events.
 */
function queueDataLayerEvents(events, targetPage, deduplicationKey = null) {
    const eventsToQueue = Array.isArray(events) ? events : [events];
    const eventIds = eventsToQueue.map(function () {
        return createEventId();
    });

    if (eventsToQueue.length === 0) {
        return true;
    }

    try {
        const now = Date.now();
        let queue = readQueue(window.sessionStorage, now);
        let replacedEntries = [];

        if (deduplicationKey !== null) {
            replacedEntries = queue.filter(function (entry) {
                return entry.deduplicationKey === deduplicationKey;
            });
            queue = queue.filter(function (entry) {
                return entry.deduplicationKey !== deduplicationKey;
            });
        }

        queue.push({
            deduplicationKey,
            eventIds,
            events: eventsToQueue,
            expiresAt: now + EVENT_LIFETIME_MS,
            targetPage,
        });

        writeQueue(window.sessionStorage, queue);

        replacedEntries.forEach(function (entry) {
            entry.events.forEach(function (eventData, index) {
                recordDiagnostic('queue_replaced', {
                    eventId: entry.eventIds[index],
                    eventName: eventData.event,
                    source: 'navigation_queue',
                    targetPage: entry.targetPage,
                });
            });
        });

        eventsToQueue.forEach(function (eventData, index) {
            recordDiagnostic('queued', {
                eventId: eventIds[index],
                eventName: eventData.event,
                source: 'navigation_queue',
                targetPage,
            });
        });

        return true;
    } catch (error) {
        eventsToQueue.forEach(function (eventData, index) {
            recordDiagnostic('storage_fallback', {
                eventId: eventIds[index],
                eventName: eventData.event,
                source: 'navigation_queue',
                targetPage,
            });
            pushDataLayerEvent(eventData, {
                eventId: eventIds[index],
                source: 'storage_fallback',
                targetPage,
            });
        });
        return false;
    }
}

/**
 * Removes matching entries before pushing them, guaranteeing at-most-once
 * emission even if the destination page is refreshed immediately afterwards.
 */
function flushDataLayerEvents(targetPage) {
    try {
        const queue = readQueue(window.sessionStorage, Date.now());
        const entriesToFlush = queue.filter(function (entry) {
            return entry.targetPage === TARGET_PAGE.ANY || entry.targetPage === targetPage;
        });
        const remainingEntries = queue.filter(function (entry) {
            return entry.targetPage !== TARGET_PAGE.ANY && entry.targetPage !== targetPage;
        });

        writeQueue(window.sessionStorage, remainingEntries);

        entriesToFlush.forEach(function (entry) {
            entry.events.forEach(function (eventData, index) {
                recordDiagnostic('queue_flushed', {
                    eventId: entry.eventIds[index],
                    eventName: eventData.event,
                    source: 'navigation_queue',
                    targetPage: entry.targetPage,
                });
                pushDataLayerEvent(eventData, {
                    eventId: entry.eventIds[index],
                    source: 'navigation_queue',
                    targetPage: entry.targetPage,
                });
            });
        });

        return entriesToFlush.reduce(function (eventCount, entry) {
            return eventCount + entry.events.length;
        }, 0);
    } catch (error) {
        recordDiagnostic('queue_flush_error', {
            source: 'navigation_queue',
            targetPage,
        });
        return 0;
    }
}

module.exports = {
    TARGET_PAGE,
    flushDataLayerEvents,
    queueDataLayerEvents,
};
